import React, { ReactElement, useEffect, useState } from 'react'

interface InfiniteScrollerProps {
  hasMore: boolean
  pxThreshold: number
  loadMore: () => Promise<void>
  lastResultRef: React.RefObject<HTMLElement>
}

/**
 * Lightweight infinite scroll implementation that supports IE 11
 * @param hasMore - True if there are more results to load.
 * @param pxThreshold - Pixels from bottom of page before the next set of results starts loading.
 * @param loadMore - Callback that is triggered when the bottom of the results have been reached.
 * @param lastResultRef - A ref to an object at the bottom of the page. This is what the pxThreshold will measure against.
 * @constructor
 */
export const InfiniteScroller = ({
  hasMore,
  pxThreshold,
  loadMore,
  lastResultRef,
}: InfiniteScrollerProps): ReactElement | null => {
  const [isLoading, setIsLoading] = useState(false)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const onScroll = () => {
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop
      setOffset(scrollTop)
    }
    window.addEventListener('scroll', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    if (!lastResultRef.current) {
      return
    }

    const lastRefY =
      window.pageYOffset + lastResultRef.current.getBoundingClientRect().top
    const thresholdReached =
      offset + window.innerHeight > lastRefY - pxThreshold

    if (hasMore && thresholdReached && !isLoading) {
      setIsLoading(true)
      loadMore().finally(() => setIsLoading(false))
    }
  }, [hasMore, isLoading, lastResultRef, loadMore, offset, pxThreshold])
  return null
}
